import imageCompression from "browser-image-compression";
import { useEffect, useState } from "react";
import { CgClose } from "react-icons/cg";
import { FaCloudUploadAlt } from "react-icons/fa";
import { MdDelete } from "react-icons/md";
import { toast } from "react-toastify";
import SummaryApi from "../common";
import productCategory from "../helpers/productCategory";
import { request } from "../helpers/request";
import uploadImage from "../helpers/uploadImage";
import DisplayImage from "./DisplayImage";

const UploadProduct = ({ onClose, fetchData }) => {
  const [data, setData] = useState({
    productName: "",
    brandName: "",
    category: "",
    productImage: [],
    description: "",
    shortDescription: "",
    price: "",
    sellingPrice: "",
    stock: "",
    hasVariants: false,
    variants: [],
    isBestSeller: false,
    isNewIn: false,
  });
  const [openFullScreenImage, setOpenFullScreenImage] = useState(false);
  const [fullScreenImage, setFullScreenImage] = useState("");
  const [removeBackground, setRemoveBackground] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const [allCategory, setAllCategory] = useState([]);
  const [allBrand, setAllBrand] = useState([]);

  const handleOnChange = (e) => {
    const { name, value } = e.target;

    setData((preve) => {
      return {
        ...preve,
        [name]: value,
      };
    });
  };

  const handleAddVariant = () => {
    setData((prev) => ({
      ...prev,
      variants: [
        ...prev.variants,
        { size: "", color: "", sku: "", price: "", sellingPrice: "", stock: 0 },
      ],
    }));
  };

  const handleVariantChange = (index, field, value) => {
    const newVariants = [...data.variants];
    newVariants[index][field] = value;
    setData((prev) => ({ ...prev, variants: newVariants }));
  };

  const handleRemoveVariant = (index) => {
    const newVariants = [...data.variants];
    newVariants.splice(index, 1);
    setData((prev) => ({ ...prev, variants: newVariants }));
  };

  const fetchCategoryAndBrand = async () => {
    try {
      const [catResponse, brandResponse] = await Promise.all([
        request(SummaryApi.getAllCategories),
        request(SummaryApi.getAllBrandsAdmin),
      ]);

      const catData = await catResponse.json();
      const brandData = await brandResponse.json();

      if (catData.success) {
        // Merge legacy categories with new ones, avoiding duplicates if necessary (simplified here)
        const legacyCategories = productCategory;
        const newCategories = catData.data.map((c) => ({
          label: c.name,
          value: c.name,
        })); // Mapping to match structure
        setAllCategory([...legacyCategories, ...newCategories]);
      }

      if (brandData.success) {
        setAllBrand(brandData.data);
      }
    } catch (error) {
      console.error("Error fetching categories or brands:", error);
    }
  };

  useEffect(() => {
    fetchCategoryAndBrand();
  }, []);

  const handleUploadProduct = async (e) => {
    const file = e.target.files[0];

    if (!file) {
      toast.error("No file selected!");
      return;
    }

    setIsUploading(true);

    try {
      let fileToUpload = file;

      // Compress for background removal if over 1MB
      if (file.size > 1024 * 1024) {
        const options = {
          maxSizeMB: 1,
          maxWidthOrHeight: 1920,
          useWebWorker: true,
        };
        fileToUpload = await imageCompression(file, options);
      }

      if (removeBackground) {
        const formData = new FormData();
        formData.append("image", fileToUpload);

        const response = await request(SummaryApi.removeBackground, {
          body: formData,
          headers: {},
        });

        const result = await response.json();

        if (result.success) {
          toast.success(`Background removed using ${result.data.service}!`);
          setData((prev) => ({
            ...prev,
            productImage: [...prev.productImage, result.data.url],
          }));
        } else if (result.quotaExceeded) {
          toast.warning(
            "Background removal quota exceeded. Uploading original image...",
          );
          const uploadImageCloudinary = await uploadImage(fileToUpload, false);

          if (uploadImageCloudinary?.url) {
            setData((prev) => ({
              ...prev,
              productImage: [...prev.productImage, uploadImageCloudinary.url],
            }));
            toast.info(
              "Image uploaded. It will be processed when quota resets.",
            );
          }
        } else {
          toast.error(result.message || "Background removal failed");
        }
      } else {
        const uploadImageCloudinary = await uploadImage(fileToUpload, false);

        if (!uploadImageCloudinary || !uploadImageCloudinary.url) {
          toast.error("Failed to upload image. Please try again.");
          return;
        }

        setData((prev) => ({
          ...prev,
          productImage: [...prev.productImage, uploadImageCloudinary.url],
        }));
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("An error occurred during upload");
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteProductImage = async (index) => {
    const newProductImage = [...data.productImage];
    newProductImage.splice(index, 1);

    setData((preve) => {
      return {
        ...preve,
        productImage: [...newProductImage],
      };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const response = await request(SummaryApi.uploadProduct, {
      body: JSON.stringify(data),
    });

    const responseData = await response.json();

    if (responseData.success) {
      toast.success(responseData?.message);
      onClose();
      fetchData();
    }

    if (responseData.error) {
      toast.error(responseData?.message);
    }
  };

  return (
    <div className="fixed inset-0 w-full h-full bg-slate-200 bg-opacity-35 flex justify-center items-center z-50 p-0 sm:p-4">
      <div className="bg-white w-full sm:max-w-2xl h-full sm:h-fit sm:max-h-[90vh] overflow-hidden relative shadow-premium sm:rounded-3xl flex flex-col">
        <div className="flex justify-between items-center px-8 py-6 border-b border-surface-border">
          <h2 className="font-bold text-lg">Upload Product</h2>
          <div
            className="w-fit ml-auto text-2xl hover:text-red-600 cursor-pointer"
            onClick={onClose}
          >
            <CgClose />
          </div>
        </div>

        <form
          className="grid p-8 gap-2 overflow-y-scroll h-full pb-12"
          onSubmit={handleSubmit}
        >
          <label htmlFor="productName">Product Name :</label>
          <input
            type="text"
            id="productName"
            placeholder="enter product name"
            name="productName"
            value={data.productName}
            onChange={handleOnChange}
            className="p-2 bg-slate-100 border rounded"
            required
          />

          <label htmlFor="brandName" className="mt-3">
            Brand Name :
          </label>
          <select
            required
            value={data.brandName}
            name="brandName"
            onChange={handleOnChange}
            className="p-2 bg-slate-100 border rounded"
          >
            <option value={""}>Select Brand</option>
            {allBrand.map((el, index) => {
              return (
                <option value={el.name} key={el._id}>
                  {el.name}
                </option>
              );
            })}
          </select>

          <label htmlFor="category" className="mt-3">
            Category :
          </label>
          <select
            required
            value={data.category}
            name="category"
            onChange={handleOnChange}
            className="p-2 bg-slate-100 border rounded"
          >
            <option value={""}>Select Category</option>
            {allCategory.map((el, index) => {
              return (
                <option value={el.value} key={el.value + index}>
                  {el.label}
                </option>
              );
            })}
          </select>
          <label htmlFor="productImage" className="mt-3 font-semibold">
            Product Image:
          </label>

          <label htmlFor="uploadImageInput" className="cursor-pointer">
            <div className="p-2 bg-slate-100 border rounded h-32 w-full flex justify-center items-center relative overflow-hidden">
              {isUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin"></div>
                  <p className="text-sm font-bold text-primary animate-pulse">
                    {removeBackground
                      ? "Removing Background..."
                      : "Uploading..."}
                  </p>
                </div>
              ) : (
                <div className="text-slate-500 flex flex-col items-center gap-2">
                  <span className="text-4xl">
                    <FaCloudUploadAlt />
                  </span>
                  <p className="text-sm">Upload Product Image</p>
                </div>
              )}
              <input
                type="file"
                id="uploadImageInput"
                className="hidden"
                accept="image/*"
                onChange={handleUploadProduct}
                disabled={isUploading}
              />
            </div>
          </label>

          <div className="flex items-center gap-3 mt-1">
            <input
              type="checkbox"
              id="bgRemoval"
              checked={removeBackground}
              onChange={(e) => setRemoveBackground(e.target.checked)}
              className="w-4 h-4 accent-primary cursor-pointer"
            />
            <label
              htmlFor="bgRemoval"
              className="text-sm font-bold text-gray-600 cursor-pointer flex items-center gap-2"
            >
              AI Background Removal{" "}
              <span className="text-[10px] bg-accent/10 text-accent px-2 py-0.5 rounded-full uppercase">
                Premium AI
              </span>
            </label>
          </div>

          <div className="mt-2 min-h-[90px]">
            {data.productImage.length > 0 ? (
              <div className="flex flex-wrap gap-3">
                {data.productImage.map((el, index) => (
                  <div
                    key={index}
                    className="relative group w-20 h-20 bg-slate-100 border rounded-md overflow-hidden"
                  >
                    <img
                      src={el}
                      alt={`Product ${index + 1}`}
                      className="w-full h-full object-scale-down cursor-pointer"
                      onClick={() => {
                        setOpenFullScreenImage(true);
                        setFullScreenImage(el);
                      }}
                    />
                    {}
                    <div
                      className="absolute bottom-0 right-0 p-1 text-white bg-red-600 rounded-full hidden group-hover:block cursor-pointer"
                      onClick={() => handleDeleteProductImage(index)}
                    >
                      <MdDelete size={16} />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-red-600 text-xs">
                *Please upload a product image
              </p>
            )}
          </div>

          <label htmlFor="price" className="mt-3">
            Price :
          </label>
          <input
            type="number"
            id="price"
            placeholder="enter price"
            value={data.price}
            name="price"
            onChange={handleOnChange}
            className="p-2 bg-slate-100 border rounded"
            required
          />

          <label htmlFor="sellingPrice" className="mt-3">
            Selling Price :
          </label>
          <input
            type="number"
            id="sellingPrice"
            placeholder="enter selling price"
            value={data.sellingPrice}
            name="sellingPrice"
            onChange={handleOnChange}
            className="p-2 bg-slate-100 border rounded"
            required
          />

          <label htmlFor="stock" className="mt-3">
            Stock :
          </label>
          <input
            type="number"
            id="stock"
            placeholder="enter stock quantity"
            value={data.stock}
            name="stock"
            onChange={handleOnChange}
            className="p-2 bg-slate-100 border rounded"
          />

          <div className="flex items-center gap-3 mt-4">
            <input
              type="checkbox"
              id="hasVariants"
              name="hasVariants"
              checked={data.hasVariants}
              onChange={(e) =>
                setData((prev) => ({ ...prev, hasVariants: e.target.checked }))
              }
              className="w-4 h-4 accent-primary"
            />
            <label
              htmlFor="hasVariants"
              className="font-bold text-sm text-gray-700"
            >
              This product has variants (Size, Color, etc.)
            </label>
          </div>

          {data.hasVariants && (
            <div className="mt-4 p-4 bg-slate-50 rounded-2xl border border-dashed border-slate-300 space-y-4">
              <h3 className="font-bold text-xs uppercase tracking-widest text-slate-400">
                Variant Options
              </h3>
              <div className="space-y-3">
                {data.variants.map((v, index) => (
                  <div
                    key={index}
                    className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 bg-white border border-slate-200 rounded-xl relative shadow-sm"
                  >
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-400 uppercase">
                        Size
                      </label>
                      <input
                        placeholder="e.g. XL, 42"
                        value={v.size}
                        onChange={(e) =>
                          handleVariantChange(index, "size", e.target.value)
                        }
                        className="w-full p-2 text-xs border border-slate-100 rounded-lg outline-none focus:border-primary"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-400 uppercase">
                        Color
                      </label>
                      <input
                        placeholder="e.g. Red"
                        value={v.color}
                        onChange={(e) =>
                          handleVariantChange(index, "color", e.target.value)
                        }
                        className="w-full p-2 text-xs border border-slate-100 rounded-lg outline-none focus:border-primary"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-400 uppercase">
                        SKU
                      </label>
                      <input
                        placeholder="SKU"
                        value={v.sku}
                        onChange={(e) =>
                          handleVariantChange(index, "sku", e.target.value)
                        }
                        className="w-full p-2 text-xs border border-slate-100 rounded-lg outline-none focus:border-primary"
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-400 uppercase">
                        M.R.P
                      </label>
                      <input
                        type="number"
                        placeholder="Price"
                        value={v.price}
                        onChange={(e) =>
                          handleVariantChange(index, "price", e.target.value)
                        }
                        className="w-full p-2 text-xs border border-slate-100 rounded-lg outline-none focus:border-primary"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-400 uppercase">
                        Selling
                      </label>
                      <input
                        type="number"
                        placeholder="Selling"
                        value={v.sellingPrice}
                        onChange={(e) =>
                          handleVariantChange(
                            index,
                            "sellingPrice",
                            e.target.value,
                          )
                        }
                        className="w-full p-2 text-xs border border-slate-100 rounded-lg outline-none focus:border-primary"
                        required
                      />
                    </div>
                    <div className="space-y-1">
                      <label className="text-[10px] font-bold text-slate-400 uppercase">
                        Stock
                      </label>
                      <input
                        type="number"
                        placeholder="Stock"
                        value={v.stock}
                        onChange={(e) =>
                          handleVariantChange(index, "stock", e.target.value)
                        }
                        className="w-full p-2 text-xs border border-slate-100 rounded-lg outline-none focus:border-primary"
                      />
                    </div>
                    <button
                      type="button"
                      onClick={() => handleRemoveVariant(index)}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center shadow-lg active:scale-90 transition-transform"
                    >
                      <CgClose size={14} />
                    </button>
                  </div>
                ))}
              </div>
              <button
                type="button"
                onClick={handleAddVariant}
                className="w-full py-3 bg-white border border-slate-200 text-slate-500 text-xs font-black uppercase tracking-widest rounded-xl hover:bg-slate-100 transition-all border-dashed"
              >
                + Add Variant Option
              </button>
            </div>
          )}

          <label htmlFor="description" className="mt-3">
            Description :
          </label>
          <textarea
            className="h-28 bg-slate-100 border resize-none p-1"
            placeholder="enter product description"
            rows={3}
            onChange={handleOnChange}
            name="description"
            value={data.description}
          ></textarea>

          {/* ── Design System fields ─────────────────────────────────────── */}
          <label htmlFor="shortDescription" className="mt-3">
            Short Description{" "}
            <span className="text-xs text-gray-400">
              (max 300 chars — shown on product cards)
            </span>{" "}
            :
          </label>
          <textarea
            className="h-16 bg-slate-100 border resize-none p-1"
            placeholder="Brief 1–2 sentence product teaser"
            rows={2}
            onChange={handleOnChange}
            name="shortDescription"
            value={data.shortDescription}
            maxLength={300}
            id="shortDescription"
          />

          <div className="flex items-center gap-6 mt-3">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="isBestSeller"
                checked={!!data.isBestSeller}
                onChange={(e) =>
                  setData((prev) => ({
                    ...prev,
                    isBestSeller: e.target.checked,
                  }))
                }
                className="w-4 h-4 accent-red-600"
              />
              <span className="text-sm font-semibold">BEST SELLER badge</span>
              <span className="text-xs text-gray-400">
                (amber badge on homepage card)
              </span>
            </label>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                name="isNewIn"
                checked={!!data.isNewIn}
                onChange={(e) =>
                  setData((prev) => ({ ...prev, isNewIn: e.target.checked }))
                }
                className="w-4 h-4 accent-red-600"
              />
              <span className="text-sm font-semibold">NEW IN badge</span>
              <span className="text-xs text-gray-400">
                (black badge on homepage card)
              </span>
            </label>
          </div>

          <button className="px-3 py-2 bg-red-600 text-white mb-10 hover:bg-red-700">
            Upload Product
          </button>
        </form>
      </div>

      {}
      {openFullScreenImage && (
        <DisplayImage
          onClose={() => setOpenFullScreenImage(false)}
          imgUrl={fullScreenImage}
        />
      )}
    </div>
  );
};

export default UploadProduct;
